import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Grid, TextField, InputAdornment } from '@mui/material';

import { ProductTypes } from 'common/types';
import { constrain } from 'common/utils/common';

import useDebouncedForm from 'common/hooks/useDebouncedForm';

import ContainerBox from 'components/ContainerBox';
import BookingBufferSelector from 'components/BookingBufferSelector';
import StartTimeSlots from 'components/StartTimeSlots';

import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as ActiveProductActions from 'actions/ActiveProductActions';
import { useTranslation } from 'services/localization/useTranslation';

const Online = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);
	const initialStartTimeSlots = useSelector(ActiveProductSelectors.startTimeSlots);
	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				bookingBuffer: product?.bookingBuffer,
				startTimeSlots: initialStartTimeSlots,
				onlineLimit: product?.onlineLimit,
			}),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const parsedValue = value === '' ? null : parseFloat(event.target.value) / 100;
		editState({
			onlineLimit: parsedValue === null ? null : constrain(parsedValue, 0, 1),
		});
	};

	return (
		<>
			<ContainerBox>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6">
							{t('product.advancedSettings', 'Advanced settings')}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography>{t('category.header', 'Reservations must be made latest')}:</Typography>
						<BookingBufferSelector
							value={state.bookingBuffer}
							onChange={(bookingBuffer) => editState({ bookingBuffer })}
							disabled={!!isEditingDisabled}
						/>
					</Grid>
				</Grid>
			</ContainerBox>
			{!product?.set && product?.type !== ProductTypes.ACCESS && (
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant="h6" gutterBottom>
								{t('product.onlineAvailability.title', 'Online availability')}
							</Typography>
							<Typography variant="body1">
								{t(
									'product.onlineAvailability.description',
									'Prevent online bookings for a given time period after the specified percentage of	capacity has been booked',
								)}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<TextField
								id="percentage"
								name="percentage"
								variant="outlined"
								label="Percentage"
								margin="normal"
								placeholder="100"
								fullWidth
								value={
									!!state.onlineLimit || state.onlineLimit === 0
										? Math.round(state.onlineLimit * 100)
										: ''
								}
								onChange={handlePercentageChange}
								disabled={isEditingDisabled}
								InputProps={{
									endAdornment: <InputAdornment position="end">%</InputAdornment>,
								}}
							/>
						</Grid>
					</Grid>
				</ContainerBox>
			)}
			<ContainerBox>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6">{t('product.startTimes.title', 'Start times')}</Typography>
					</Grid>
					<Grid item xs={12}>
						<StartTimeSlots
							value={state.startTimeSlots}
							onChange={(startTimeSlots) => {
								editState({ startTimeSlots });
							}}
							disabled={!!isEditingDisabled}
						/>
					</Grid>
				</Grid>
			</ContainerBox>
		</>
	);
};

export default Online;
